import React, { useMemo } from 'react';
import { usePartsStatus } from '../usePartsStatus';
import useFleetStore from '../../stores/useFleetStore';
import Panel from '../../components/ui/Panel';
import EmptyState from '../../components/ui/EmptyState';
import Tag from '../../components/ui/Tag';
import { CATEGORY } from '../../components/ui/categoryColors';
import { IconCheck } from '../../components/ui/icons';
import { useTicker } from '../../components/ui/motion';

// Hue order shared with FlagSummaryModule and the ShopFloor flag panel
const FLAG_HUES = ['orange', 'red', 'yellow', 'purple', 'blue', 'pink', 'teal', 'indigo'];
const flagColour = (i) => CATEGORY[FLAG_HUES[i % FLAG_HUES.length]].mark.oklch;

// A hero figure with its small label under it, centred in its half of the card.
const Figure = ({ value, label, hue }) => {
  const shown = useTicker(typeof value === 'number' ? value : 0);
  return (
    <div className="min-w-0 text-center">
      <div
        className="text-xl font-bold tracking-tight tabular-nums sm:text-3xl"
        style={{ color: hue ? CATEGORY[hue].ink.oklch : 'var(--on-surface-variant)' }}
      >
        {typeof value === 'number' ? shown : value}
      </div>
      <div className="mt-0.5 text-xs text-on-surface-variant">{label}</div>
    </div>
  );
};

const StatCardsModule = ({ fleetCounts, available }) => {
  const { data: partsStatus } = usePartsStatus();
  const flags = useFleetStore((state) => state.flags);
  // Phase 1b: battery_total is the sum of parts_inventory.quantity across
  // every gen2_battery family SKU. Replaces the legacy ffpartnum='BATTERY'
  // single-row lookup. Until partsStatus resolves, falls back to 0 — same
  // graceful-empty behavior the legacy widget gave when the store was loading.
  const batteryQty = partsStatus?.battery_total ?? 0;

  // Group flags by description/category — same logic as FlagSummaryModule
  const flagCategories = useMemo(() => {
    const cats = flags.reduce((acc, f) => {
      const cat = f.descript || 'Uncategorized';
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(cats).sort((a, b) => b[1] - a[1]);
  }, [flags]);

  // Drone ink and the card's accent follow availability health; batteries are always good
  const droneHue = available > 0 ? 'green' : available != null ? 'red' : null;
  const cardAccent = available != null && available <= 0 ? 'red' : 'green';

  return (
    <div className="grid grid-cols-2 gap-2 sm:gap-4">
      {/* ── Available for Shows (drones + batteries in one card) ── */}
      <Panel
        title="Available for Shows"
        titleStyle="section"
        accent={cardAccent}
        lit={available != null}
        level={3}
        actions={<span aria-hidden="true" className="flex text-on-surface-disabled"><IconCheck className="h-5 w-5" /></span>}
      >
        <div role="group" aria-label="Available for shows" className="mt-2 grid grid-cols-2 divide-x divide-outline-variant">
          <Figure label="Drones" value={available != null ? available : '\u2014'} hue={droneHue} />
          <Figure label="Batteries" value={batteryQty} hue="green" />
        </div>
        <div className="mt-3 text-center text-xs leading-tight text-on-surface-variant">
          Internal minus leased, rented &amp; flagged
        </div>
      </Panel>

      {/* ── Flagged (stacked bar + legend, like /shop flag summary) ── */}
      <Panel
        title="Flagged"
        titleStyle="section"
        accent="orange"
        lit
        level={3}
        actions={flags.length > 0 ? <Tag size="sm" hex={CATEGORY.orange.ink.hex}>{flags.length} total</Tag> : undefined}
      >
        {flags.length === 0 ? (
          <EmptyState variant="area" dense>No active flags</EmptyState>
        ) : (
          <>
            {/* Stacked bar */}
            <div className="flex rounded-full h-2.5 overflow-hidden mt-3 mb-3 bg-gray-900/60">
              {flagCategories.map(([cat, count], i) => (
                <div key={cat}
                     className="transition-all duration-700"
                     style={{ background: flagColour(i), width: `${(count / flags.length) * 100}%`, minWidth: '3px' }}
                     title={`${cat}: ${count}`} />
              ))}
            </div>
            {/* Legend (top 4) */}
            <div className="space-y-1">
              {flagCategories.slice(0, 4).map(([cat, count], i) => (
                <div key={cat} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: flagColour(i) }} />
                  <span className="text-xs text-on-surface flex-1 truncate">{cat}</span>
                  <span className="text-xs text-on-surface-variant tabular-nums font-medium">{count}</span>
                </div>
              ))}
              {flagCategories.length > 4 && (
                <div className="text-[10px] text-on-surface-disabled pl-4">+{flagCategories.length - 4} more</div>
              )}
            </div>
          </>
        )}
      </Panel>
    </div>
  );
};

export default StatCardsModule;
